import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Tooltip, Badge } from 'seagull';
import { ServiceAdvancedSettings } from '~/components/ServiceAdvancedSettings';
import { useIoContext } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { AgreementDataModel, serviceModel } from '~/models';
import { useValidateService } from '~/models/service/hooks/useValidateService';
import { billingBadgesTextsPath, getServiceDescriptionBadges } from '~/utils/service-description/getServiceDescriptionBadges';
import EditableTableCell from '../EditableTableCell/EditableTableCell';
import styles from './AdvancedSettingsCell.module.scss';
function AdvancedSettingsModalContent(param) {
    let { serviceId, draft, wasSubmitted, setDraft } = param;
    const featureFlags = useFeatureFlags();
    const service = useMemo(()=>draft.service(serviceId), [
        draft,
        serviceId
    ]);
    function changeService(transformer) {
        setDraft((prev)=>prev.setService(serviceId, transformer));
    }
    return service ? /*#__PURE__*/ _jsx("div", {
        className: styles.modalContent,
        children: /*#__PURE__*/ _jsx(ServiceAdvancedSettings, {
            isFreshlyAdded: false,
            isOpenInitially: true,
            hideTitle: true,
            service: service,
            testId: "adv-settings-modal",
            showServiceValidationWarning: wasSubmitted,
            relationshipState: {
                type: 'awaitingApproval',
                effectiveDate: draft.effectiveDateAsDate()
            },
            isReadOnly: false,
            onPausedChange: (_, paused)=>changeService((s)=>serviceModel.setPaused(s, paused)),
            onBillOnAcceptanceChange: (_, billOnAcceptance)=>changeService((s)=>serviceModel.setSkipBillingOnAcceptance(s, !billOnAcceptance, featureFlags)),
            onProratedChange: (_, prorated)=>changeService((s)=>serviceModel.setProrated(s, prorated)),
            onOptionalChange: (_, optional, includedByDefault)=>changeService((s)=>serviceModel.setParticipation(s, optional, includedByDefault)),
            onLimitBillingTimesChange: (_, enabled, limit)=>changeService((s)=>serviceModel.setMaxCharges(s, enabled, limit)),
            onAutoPriceIncreaseChange: (_, priceIncrease)=>changeService((s)=>serviceModel.setPriceIncrease(s, priceIncrease)),
            onQboClassChange: (_, integratedClass)=>changeService((s)=>serviceModel.setIntegratedClass(s, integratedClass)),
            onKarbonChange: (_, karbon, options)=>changeService((s)=>serviceModel.setKarbonIntegration(s, karbon, options)),
            onFinancialCentsChange: (_, financialCents, options)=>changeService((s)=>serviceModel.setFinancialCentsIntegration(s, financialCents, options)),
            onLimitedTimeDiscountOfferChange: (_, limitedTimeDiscountOffer)=>{
                changeService((s)=>serviceModel.setLimitedTimeDiscountOffer(s, limitedTimeDiscountOffer));
            },
            onDiscountPeriodExpirationChange: (_, discountPeriodExpiration)=>{
                changeService((s)=>serviceModel.setDiscountPeriodExpiration(s, discountPeriodExpiration));
            },
            onPreapprovedAmountChange: (_, amount, controlledByAdvancedSettings)=>{
                changeService((s)=>serviceModel.setPreapprovedAmount(s, amount, controlledByAdvancedSettings));
            },
            onStartBillingAtChange: (_, startBillingAt)=>{
                changeService((s)=>serviceModel.setStartBillingAt(s, startBillingAt));
            },
            onServiceChange: (updatedService)=>{
                changeService((_s)=>updatedService);
            }
        })
    }) : null;
}
export default function AdvancedSettingsCell(props) {
    const { service, draft, onOpenModal, isDisabled, disabledTooltip } = props;
    const { getNow } = useIoContext();
    const featureFlags = useFeatureFlags();
    const { isInvalid } = useValidateService();
    const { t: billingBadgesTexts } = useLocaleKeys(billingBadgesTextsPath);
    const badgesList = useMemo(()=>getServiceDescriptionBadges(service, AgreementDataModel.fromDraft(draft), getNow(), featureFlags, {
            t: billingBadgesTexts,
            hideDiscountBadge: true
        }), [
        service,
        draft,
        featureFlags
    ]);
    const handleEdit = ()=>{
        onOpenModal({
            width: '684px',
            renderContent: (p)=>({
                    title: 'Service advanced settings',
                    testId: 'advanced-settings-modal',
                    content: /*#__PURE__*/ _jsx(AdvancedSettingsModalContent, _object_spread_props(_object_spread({}, p), {
                        serviceId: service.id
                    })),
                    onSubmit: (defaultBehavior)=>{
                        defaultBehavior();
                    }
                }),
            shouldSubmit: async (editedDraft)=>{
                const editedService = editedDraft.service(service.id);
                if (!editedService) return Promise.resolve(false);
                const isServiceInvalid = isInvalid(editedService, {
                    relationshipEffectiveDate: editedDraft.effectiveDateAsDate(),
                    isTemplate: false
                });
                return Promise.resolve(!isServiceInvalid);
            }
        });
    };
    return /*#__PURE__*/ _jsx(EditableTableCell, {
        className: styles.root,
        onClick: handleEdit,
        testId: "edit-advanced-settings-".concat(service.id),
        isDisabled: isDisabled,
        disabledTooltip: disabledTooltip,
        children: /*#__PURE__*/ _jsx("div", {
            className: styles.badges,
            children: badgesList.map((param)=>{
                let { text, intent, skin, tooltip } = param;
                return /*#__PURE__*/ _jsx(Tooltip, {
                    title: tooltip || '',
                    placement: "top",
                    children: /*#__PURE__*/ _jsx(Badge, {
                        rounded: true,
                        outlined: true,
                        skin: skin,
                        testId: "advanced-settings-cell-badge-".concat(intent),
                        children: text
                    })
                }, intent);
            })
        })
    });
}
